import React, { useState, useEffect, useCallback } from "react";
import { usePermissionContext } from "../../contexts/permission-context";
import Filter from "../../components/Filter/Filter";
import ActionDialog from "../../components/Dialogs/ActionDialog";
import { FormDialog } from "../../components/FormDialog/FormDialog";
import ConfirmDialog from "../../components/Dialogs/ConfirmDialog";
import {
    platformPermissionOptions, fetchAPIData, generateHiddenColumns,
    copyToClipboard, hasPermission, platformManage,
    getCompanyId, getToken, getUser,createFilterOptions, createSortFieldOptions
} from '../../Utils/Common';
import Table, { renderCellExpand } from '../../components/Table/Table';
import { usePersistentStorage } from "../../Utils/usePersistentStorage";
import { GridActionsCellItem } from "@mui/x-data-grid";
import CopyIcon from '@mui/icons-material/AddToPhotosOutlined';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import axios from 'axios';
import './system.scss';

const cspReportsSchema = require("../../json/cspReportsSchema.json").view;

const SystemCspReportsTabPanel = ({ setNotify }) => {

    const { permissions } = usePermissionContext();

    const [apiData, setApiData] = useState({});
    const [rowJsonData, setRowJsonData] = useState({});
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('view');  // default to view mode

    const [reload, setReload] = useState(false);

    const [isLoading, setIsloading] = useState(false);

    const [confirmOpen, setConfirmOpen] = useState(false);

    const [confirmProps, setConfirmProps] = useState({});

    const [cspReports, setCspReports] = usePersistentStorage("cspReports", []);

    useEffect(() => {
        setApiData(cspReports);
    }, []);

    // API URL is dynamic in that COMPANY_ID is replaced
    const apiURL = '/api/companies/%COMPANY_ID%/reportsTracking?';

    const fieldData = [
        'name', 'path', 'reportType', 'error', 'reportDate', 'reportGeneratedDate', 'createdDate', 'createdUserId'
    ];

    const hasPlatformManage = hasPermission(permissions, platformManage);

    const sortFieldData = [
        // {
        //     field: 'companyId',
        //     label: 'Company ID'
        // },
        {
            field: 'name',
            label: 'Name'
        },
        {
            field: 'path',
            label: 'Path'
        },
        {
            field: 'reportDate',
            label: 'Report Date'
        },
        {
            field: 'reportGeneratedDate',
            label: 'Report Generated Date'
        },
        {
            field: 'reportType',
            label: 'Report Type'
        },
        {
            field: 'createdDate',
            label: 'Created Date'
        },
        {
            field: 'createdUserId',
            label: 'Created User ID'
        }
    ];

    const totalLabel = "Matching CSP Reports";

    const getActionButtons = (params) => {
        let actionButtons = [];

        const isEmpty = (str) => (!str?.length);

        if (!isEmpty(params.row.path)) {
            let downloadReportButton = <GridActionsCellItem icon={
                <Tooltip title="Download Report" arrow placement="right">
                    <FileDownloadIcon />
                </Tooltip>}
                label='Download Report'
                onClick={downloadReportData(params.row)}
            />;

            hasPlatformManage && actionButtons.push(downloadReportButton);
        }

        if (!isEmpty(params.row.error)) {
            let regenerateButton = <GridActionsCellItem icon={
                <Tooltip title="Regenerate Report" arrow placement="right">
                    <RefreshIcon />
                </Tooltip>}
                label='Regenerate Report'
                onClick={regenerateReportData(params.row)}
            />;

            hasPlatformManage && actionButtons.push(regenerateButton);
        }

        return actionButtons;

    };

    const columns = [
        // {
        //     field: 'id',
        //     type: 'number',
        //     headerName: 'ID',
        //     hide: true,
        //     minWidth: 50,
        //     flex: 1,
        //     headerAlign: 'left',
        //     align: 'left',
        // },
        {
            hideable: false,
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            getActions: (params) => getActionButtons(params)
        },
        {
            hideable: false,
            field: 'companyId',
            headerName: 'Company ID',
            type: 'number',
            minWidth: 100,
            maxWidth: 100,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            valueFormatter: (params) => {
                return params.value.toString();
            }
        },
        {
            hideable: false,
            field: 'name',
            headerName: 'Report Name',
            minWidth: 220,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: "170px" }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy name" onClick={(event) => copyToClipboard(event, params.row.name)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'reportType',
            headerName: 'Report Type',
            minWidth: 220,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">
                        <div style={{ width: "170px" }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy report Type" onClick={(event) => copyToClipboard(event, params.row.reportType)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            hideable: false,
            field: 'path',
            headerName: 'Report Path',
            minWidth: 510,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {
                return (
                    <div className="guidContent">

                        <div style={{ wordBreak: 'break-word', textAlign: 'left', width: '460px' }}>
                            {params.value}
                        </div>
                        {params.value &&
                            <div>
                                <Tooltip title="Copy to clipboard" arrow placement="right">
                                    <IconButton aria-label="copy path" onClick={(event) => copyToClipboard(event, params.row.path)}>
                                        <CopyIcon sx={{ fontSize: 'small' }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        }
                    </div>
                );
            }
        },
        {
            hideable: false,
            field: 'reportDate',
            headerName: 'Report Date',
            minWidth: 225,
            //maxWidth: 300,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'reportGeneratedDate',
            headerName: 'Report Generated Date',
            minWidth: 225,
            //maxWidth: 300,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            hideable: false,
            field: 'error',
            headerName: 'Error Message',
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: (params) => {

                return (
                    <div style={{ flexDirection: 'column', display: 'inline-block', textAlign: 'left', wordBreak: 'break-word' }}>
                        {params.value}
                    </div>
                );
            }
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            hide: true,
            minWidth: 225,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand,
        },
        {
            field: 'createdUserId',
            headerName: 'Created by User ID',
            hide: true,
            minWidth: 200,
            flex: 1,
            headerAlign: 'left',
            align: 'left',
            renderCell: renderCellExpand
        }

    ];

    const getColumns = () => {
        return (hasPlatformManage && columns) || columns.filter(obj => { return obj.field !== "actions" });
    };

    const toggleColumnPinning = () => {
        return (hasPlatformManage && 'columnPinning') || '';
    };

    const regenerateReportData = useCallback((rowData) => () => {
        let reportName = rowData.name;
        let title = "Regenerate Report?";
        let confirmButtonText = "Yes";
        let cancelButtonText = "No";
        let msg = `The report ${reportName} will be regenerated. Are you sure?`;

        let confirmProps = {
            title,
            msg,
            confirmButtonText,
            cancelButtonText,
            confirmCallback: () => regenerateReport(rowData)
        };


        setConfirmProps(confirmProps);

        setConfirmOpen(true);


    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
    };

    const viewRowData = (param, event) => {

        // remove the id from table data as table data only not part of json stored
        const {
            id,
            ...JsonData
        } = param.row;

        setRowJsonData(JsonData);
        setMode('view');
        setOpen(true);
    };

    const fetchData = async (apiURL2, qparams) => {
        setIsloading(true);
        await fetchAPIData(apiURL2, qparams)
            .then((data) => {
                setApiData(data);
                setCspReports(data);
                // clear the reload false now that data is loaded
                setReload(false);
                setIsloading(false);
            })
            .catch((err) => {
                console.log(err);

                let message = `Failed to load CSP Reports data \n\n ${err.response.statusText} : ${err.response.data.message}`;
                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

                setReload(false);
                setIsloading(false);
            });

    };

    const regenerateReport = (data) => {

        let reportName = data.name;
        let companyId = data.companyId;
        let reportDate = data.reportDate;
        let reportType = data.reportType;

        //console.log("regenerate report button clicked", data);

        let regenerateReportUrl = `/api/companies/${companyId}/reports/regenerate?reportName=${reportName}&reportDate=${reportDate}&reportType=${reportType}`;

        // use loading indicator to show regenerate in progress
        setIsloading(true);

        axios({
            method: "get", url: regenerateReportUrl,
            headers: { "insightUserId": getUser(), "companyId": getCompanyId(), "Authorization": getToken() }
        })
            .then(response => {
                // clear loading indicator to show regenerate completed
                setIsloading(false);

                let message = `Report ${reportName} for company id ${companyId} has been regenerated`;

                setNotify({
                    isOpen: true,
                    message: message,
                    severity: 'info'
                });

                // reload table data after regenerate??
                setReload(true);
            }).catch(error => {
                console.log('error in regenerate report api call:', error);

                let message = `Failed to regenerate report [${reportName}] for company ID [${companyId}]: \n\n ${error.response.statusText} : ${error.response.data.message}`;

                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

            });
    };

    const downloadReportData = useCallback((rowData) => () => {
        let companyId = rowData.companyId;
        let reportName = rowData.name;
        let reportPath = rowData.path;

        let downloadApiURL = `/api/companies/${companyId}/reports/download?reportPath=${reportPath}`;

        let message = `Downloading ${reportName} report from ${reportPath}...`;

        setNotify({
            isOpen: true,
            message: message,
            severity: 'info'
        });

        axios({
            method: "get", url: downloadApiURL,
            headers: { "insightUserId": getUser(), "companyId": getCompanyId(), "Authorization": getToken() },
            responseType: 'blob'
        })
            .then((response) => {

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const filename = response.headers['content-disposition'].split('filename=')[1].replace(/"/g, "");
                link.download = !!filename ? filename : 'report.csv';
                link.click();
            }).catch(error => {
                console.log('error in download report api call:', error);

                let message = `Failed to download report [${reportName}] for company ID [${companyId}]: \n\n ${error.response.statusText} : ${error.response.data.message}`;

                setNotify({
                    isOpen: true,
                    alertTitle: 'Request failed',
                    message: message,
                    severity: 'error'
                });

            });;
    }, []);


    return (
        <>

            <div id="cspReportFilterContainer" style={{ marginTop: '0px', marginLeft: '8px' }}>
                <Filter permission={platformPermissionOptions} fields={createFilterOptions(fieldData)} sortFields={createSortFieldOptions(sortFieldData)} apiURL={apiURL} fetchData={fetchData} setNotify={setNotify} useCompanyId={true} totalLabel={totalLabel} reload={reload} tabName={'cspReportsTab'} />
            </div>


            <Divider />

            <div id="cspReportContainer" className={toggleColumnPinning()}>
                <div>
                    <Table data={apiData} columns={getColumns()} hiddenColumns={generateHiddenColumns(columns)} viewRowData={viewRowData} isLoading={isLoading} />
                </div>
            </div>

            {/* This dialog only support view. No need for form or save */}
            <ActionDialog
                open={open}
                mode={mode}
                onClose={handleClose}
                title={`CSP Reports`}
                defaultTab='JSON'
                propertiesTabContent={
                    <FormDialog
                        formId={'cspReportsDialogId'}
                        readOnly={true}
                        schema={cspReportsSchema}
                        formData={rowJsonData}
                    />
                }
                jsonDataName={`csp reports`}
                JSONTabContent={rowJsonData}
            />

            <ConfirmDialog
                title={confirmProps.title}
                open={confirmOpen}
                onClose={handleConfirmClose}
                setOpen={setConfirmOpen}
                cancelButtonText={confirmProps.cancelButtonText}
                confirmButtonText={confirmProps.confirmButtonText}
                onConfirm={confirmProps.confirmCallback}
            >
                {confirmProps.msg}
            </ConfirmDialog>
        </>
    );


};


export default SystemCspReportsTabPanel;